import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { AutoPayFormData } from '../../../components/modals/AutoPayAuthorizationModal';
import { ContractApiResponse } from '../../../services/contract-services';
import { enableAutoPay } from '../../../services/payment-services';
import { store } from '../../../store';
import { hideLoader, showLoader } from '../../../store/reducers/appSlice';
import { updateContractAutopayInProgress } from '../../../store/reducers/contractsSlice';
import { openToast } from '../../../store/reducers/toastSlice';
import { trackAutoPayEnabled, trackAutoPayFailed } from '../../../utils/google-analytics';

dayjs.extend(utc);
dayjs.extend(timezone);

// Must match the backend's America/New_York "today" check in validateAutoPayDates.
const AUTO_PAY_TIMEZONE = 'America/New_York';

export const useAutoPayAndPdfPreview = (
    contract: ContractApiResponse | null,
    isImpersonating: boolean,
    setContract: (c: ContractApiResponse) => void,
) => {
    const dispatch = useDispatch();

    const [autoPayModalOpen, setAutoPayModalOpen] = useState(false);
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

    const handleAutoPaySubmit = async (formData: AutoPayFormData) => {
        if (isImpersonating) {
            setAutoPayModalOpen(false);
            return;
        }
        try {
            dispatch(showLoader());

            // HANDLING PDF GENERATION FROM BACKEND
            const payload = {
                contractId: contract?.Id,
                startDate: formData.startDate
                    ? formData.startDate.format('YYYY-MM-DD')
                    : dayjs().tz(AUTO_PAY_TIMEZONE).format('YYYY-MM-DD'),
                bankAccountId: String(formData?.bankAccount?.Id),
                signature: formData?.borrower,
            };

            const response = await enableAutoPay(payload, true);
            if (response.statusCode === 200) {
                // Track successful autopay enablement
                trackAutoPayEnabled(contract?.AspireId || String(contract?.Id), formData?.bankAccount?.BankName);

                // Close the AutoPay modal
                setAutoPayModalOpen(false);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'success',
                    }),
                );

                // Update AutopayInProgress for the specific contract in the store
                if (contract?.Id) {
                    const contractId = contract.Id;
                    dispatch(updateContractAutopayInProgress(contractId));
                    // Filter contract from store and update local state
                    setTimeout(() => {
                        const updatedContracts = store.getState().contracts.contracts;
                        const updatedContract = updatedContracts.find(c => c.Id === contractId);
                        if (updatedContract) {
                            setContract(updatedContract);
                        }
                    }, 1000);
                }
            } else {
                // Track autopay failure
                trackAutoPayFailed(contract?.AspireId || String(contract?.Id), response.message);

                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            console.error('Error generating AutoPay PDF:', error);
            const e = error as { message?: string };
            // Track autopay failure
            trackAutoPayFailed(contract?.AspireId || String(contract?.Id), e.message || 'Unknown error');

            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e.message || 'Failed to generate AutoPay authorization document',
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleClosePdfPreview = () => {
        setPdfPreviewOpen(false);
        // Clean up the blob URL to prevent memory leaks
        if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl(null);
        }
    };

    const handleDownloadPDF = () => {
        if (pdfPreviewUrl) {
            const link = document.createElement('a');
            link.href = pdfPreviewUrl;
            link.download = 'autopay-authorization.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return {
        autoPayModalOpen,
        setAutoPayModalOpen,
        handleAutoPaySubmit,
        pdfPreviewOpen,
        pdfPreviewUrl,
        handleClosePdfPreview,
        handleDownloadPDF,
    };
};
